import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { api, errorText } from "../../lib/api";
import { SERVICES } from "../../data/site";
import { CoverUpload } from "./PostEditor";
import { AiPanel } from "./AiPanel";
import { streamAI, parseFields } from "../../lib/ai";

const EMPTY = { client: "", slug: "", industry: "", image: "/img/case1.webp", challenge: "", approach: "", results: "", metrics: [["", ""], ["", ""], ["", ""]], services: [], order: 0, published: true };
const FIELD = "mt-1.5 w-full rounded-xl border border-ink/15 bg-white px-3.5 py-2.5 text-sm text-ink outline-none focus:border-ink";

const padMetrics = (m) => [...(m || []), ["", ""], ["", ""], ["", ""]].slice(0, 3);

const Input = ({ label, name, form, set, ...rest }) => (
  <label className="block text-sm font-semibold text-ink">
    {label}
    <input value={form[name]} onChange={(e) => set((f) => ({ ...f, [name]: e.target.value }))} className={FIELD} data-testid={`case-${name}-input`} {...rest} />
  </label>
);

const Area = ({ label, name, form, set }) => (
  <label className="block text-sm font-semibold text-ink">
    {label}
    <textarea rows={2} required value={form[name]} onChange={(e) => set((f) => ({ ...f, [name]: e.target.value }))} className={FIELD} data-testid={`case-${name}-input`} />
  </label>
);

const Metrics = ({ form, set }) => {
  const update = (i, j, v) => set((f) => ({ ...f, metrics: f.metrics.map((m, k) => (k === i ? (j === 0 ? [v, m[1]] : [m[0], v]) : m)) }));
  return (
    <div>
      <p className="text-sm font-semibold text-ink">Key results <span className="font-normal text-ink/55">(up to 3, e.g. "+3.4x" / "quote requests")</span></p>
      <div className="mt-1.5 grid gap-2">
        {form.metrics.map((m, i) => (
          <div key={i} className="grid grid-cols-[110px_1fr] gap-2">
            <input value={m[0]} onChange={(e) => update(i, 0, e.target.value)} placeholder="Number" className={FIELD + " !mt-0"} data-testid={`case-metric-value-${i}`} />
            <input value={m[1]} onChange={(e) => update(i, 1, e.target.value)} placeholder="What it measures" className={FIELD + " !mt-0"} data-testid={`case-metric-label-${i}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

const ServicesPick = ({ form, set }) => {
  const toggle = (name) => set((f) => ({ ...f, services: f.services.includes(name) ? f.services.filter((s) => s !== name) : [...f.services, name] }));
  return (
    <div>
      <p className="text-sm font-semibold text-ink">Services used</p>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {SERVICES.map((s) => {
          const on = form.services.includes(s.form);
          return (
            <button type="button" key={s.slug} onClick={() => toggle(s.form)} data-testid={`case-service-${s.slug}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border border-navy bg-navy text-white" : "border border-ink/15 bg-white text-ink hover:border-ink"}`}>
              {s.title}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const CaseEditor = ({ item, onClose, onSaved }) => {
  const [form, setForm] = useState(item ? { ...EMPTY, ...item, metrics: padMetrics(item.metrics) } : EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const write = async ({ notes }) => {
    await streamAI("/admin/ai/case-study", { notes, client: form.client, industry: form.industry }, (text) => {
      const f = parseFields(text, ["CHALLENGE", "APPROACH", "RESULTS"]);
      setForm((cur) => ({ ...cur, challenge: f.CHALLENGE ?? cur.challenge, approach: f.APPROACH ?? cur.approach, results: f.RESULTS ?? cur.results }));
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const { id, created_at, updated_at, ...rest } = form;
    const payload = { ...rest, order: Number(rest.order) || 0, metrics: rest.metrics.filter(([v, l]) => v.trim() && l.trim()) };
    try {
      if (item?.id) await api.put(`/admin/case-studies/${item.id}`, payload);
      else await api.post("/admin/case-studies", payload);
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white" data-lenis-prevent data-testid="case-editor">
        <DialogHeader><DialogTitle className="font-display text-2xl">{item?.id ? "Edit case study" : "New case study"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Client name" name="client" form={form} set={setForm} required />
            <Input label="Industry" name="industry" form={form} set={setForm} required placeholder="e.g. Allied health" />
            <Input label="URL slug (optional)" name="slug" form={form} set={setForm} placeholder="auto from client name" />
            <Input label="Display order" name="order" type="number" form={form} set={setForm} />
          </div>
          <CoverUpload label="Project photo" testId="case-image" value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} />
          <AiPanel testId="ai-case" title="Write with AI" hint="Paste your rough notes and Claude fills in the challenge, approach and results. It only uses numbers from your notes."
            fields={[{ name: "notes", placeholder: "e.g. phones were quiet, we ran Google Ads + local SEO, calls doubled in 3 months", multiline: true, required: true, min: 10 }]}
            onRun={write} />
          <Area label="The challenge" name="challenge" form={form} set={setForm} />
          <Area label="Our approach" name="approach" form={form} set={setForm} />
          <Area label="The results" name="results" form={form} set={setForm} />
          <Metrics form={form} set={setForm} />
          <ServicesPick form={form} set={setForm} />
          <label className="flex items-center gap-3 text-sm font-semibold text-ink">
            <Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} data-testid="case-published-switch" /> Published
          </label>
          {error && <p className="text-sm text-flame-dark" role="alert" data-testid="case-editor-error">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="btn-ghost !py-2.5" data-testid="case-cancel-button">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary !py-2.5" data-testid="case-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save case study"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
